import type { AstroTime } from "astronomy-engine";

/**
 * Lahiri (Chitrapaksha) ayanamsha — the difference between the tropical and
 * sidereal zodiacs used by Vedic/Jyotish astrology, and the de-facto
 * industry-standard ayanamsha (see docs/reference-calculator.md section
 * 4.1, which independently re-derives it from the reference calculator's
 * output and confirms ~23.72° for a May-1990 date).
 *
 * Standard definition (matches Swiss Ephemeris SE_SIDM_LAHIRI / IAE 1985):
 * 23°15'00.658" at 21 March 1956, 00:00 TDT (JD 2435553.5 TT), plus general
 * precession since that epoch. We use the IAU 2006 general precession in
 * longitude rate (~50.28"/year, per the IAU precession-nutation model) as
 * the linear rate — Swiss Ephemeris uses a slightly more elaborate
 * (non-linear, higher-order) precession model, so this is a first-order
 * approximation. docs/reference-calculator.md's own independent check
 * against the reference tool's live JSON output confirmed this formula to
 * within ~0.003° for a 1990 date (see validate-ephemeris.mjs at repo root,
 * used for that one-time verification) — well inside typical natal-chart
 * precision needs (Rashi/Nakshatra/Pada boundaries are each 30°/13°20'/3°20'
 * wide). If sub-arcminute precision is ever required (e.g. matching a paid
 * professional tool exactly), swap this for a real Swiss Ephemeris binding
 * (see sweph-wasm on npm) — this module's single exported function is the
 * seam to do that without touching any caller.
 */
export function lahiriAyanamshaDegrees(time: AstroTime): number {
  const JD_1956_TT = 2435553.5; // 21 March 1956, 00:00 TDT
  const AYANAMSHA_AT_1956_DEG = 23 + 15 / 60 + 0.658 / 3600;
  const PRECESSION_ARCSEC_PER_YEAR = 50.28;

  const daysSince1956 = time.tt - (JD_1956_TT - 2451545.0); // AstroTime.tt is TT days since J2000
  const yearsSince1956 = daysSince1956 / 365.2425;

  return AYANAMSHA_AT_1956_DEG + (yearsSince1956 * PRECESSION_ARCSEC_PER_YEAR) / 3600;
}
